import type { PostgresMigrationDto } from '@gap/backend/app-services/postgres-migrations/models/PostgresMigrationDto'
import { postgresApiClient } from 'api/trpc-pg/trpc-client'
import Loader from 'reusable/mobx-helpers/Loader'

export default class PostgresMigrationsStore {
   statusLoader = new Loader()
   runLoader = new Loader()
   status: PostgresMigrationDto.StatusResponse | null = null
   lastRunResult: PostgresMigrationDto.RunResult | null = null
   selectedErrorIdx: number | null = null

   /**
    * Merged view of migrations: combines status from server with cached run errors.
    * If a migration is "pending" on the server but failed in our last run, show it as "failed".
    */
   get migrations(): PostgresMigrationDto.MigrationEntry[] {
      if (!this.status) return []

      if (!this.lastRunResult) return this.status.migrations

      // Build error map from last run result
      const errorMap = new Map<number, PostgresMigrationDto.ErrorDetail>()
      for (const r of this.lastRunResult.results) {
         if (!r.success && r.error) {
            errorMap.set(r.idx, r.error)
         }
      }

      return this.status.migrations.map(m => {
         const runError = errorMap.get(m.idx)
         if (m.status === 'pending' && runError) {
            return { ...m, status: 'failed' as const, error: runError }
         }
         return m
      })
   }

   get summary() {
      const migrations = this.migrations
      const applied = migrations.filter(m => m.status === 'applied').length
      const failed = migrations.filter(m => m.status === 'failed').length
      const pending = migrations.filter(m => m.status === 'pending').length
      return { total: migrations.length, applied, failed, pending }
   }

   get hasPending(): boolean {
      return this.migrations.some(m => m.status === 'pending' || m.status === 'failed')
   }

   loadStatus = async () => {
      await this.statusLoader.executeLoading(async () => {
         this.status = await postgresApiClient.postgresMigrations.getStatus.query()
      })
   }

   runPending = async () => {
      await this.runLoader.executeLoading(async () => {
         this.lastRunResult = await postgresApiClient.postgresMigrations.runPending.mutate()
         // Refresh status after running
         this.status = await postgresApiClient.postgresMigrations.getStatus.query()
      })
   }

   toggleError = (idx: number) => {
      this.selectedErrorIdx = this.selectedErrorIdx === idx ? null : idx
   }

   clearLastRunResult = () => {
      this.lastRunResult = null
   }
}
